import { useEffect, useMemo, useState, type FormEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";
import FilterBar from "@/components/feature/FilterBar";
import MediaCard from "@/components/feature/MediaCard";
import EmptyState from "@/components/feature/EmptyState";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import {
  allMedia,
  applyFilters,
  getFilterOptions,
  searchMedia,
  sortMedia,
  type MediaFilters,
  type SortKey,
} from "@/lib/media";

const emptyFilters: MediaFilters = { genres: [], regions: [], years: [] };

export default function Search() {
  const [searchParams, setSearchParams] = useSearchParams();
  const query = searchParams.get("q") ?? "";
  const [input, setInput] = useState(query);
  const [filters, setFilters] = useState<MediaFilters>(emptyFilters);
  const [sort, setSort] = useState<SortKey>("latest");

  useEffect(() => {
    setInput(query);
    setFilters(emptyFilters);
    setSort("latest");
  }, [query]);

  const matched = useMemo(() => (query ? searchMedia(query, allMedia) : []), [query]);
  const options = useMemo(() => getFilterOptions(matched), [matched]);
  const results = useMemo(
    () => sortMedia(applyFilters(matched, filters), sort),
    [matched, filters, sort],
  );

  useDocumentMeta({
    title: query ? `“${query}”搜索结果 - 91视频网站` : "搜索影视资源 - 91视频网站",
    description: query
      ? `91视频网站为你找到与“${query}”相关的影视资源，支持按类型、地区、年份进一步筛选，快速定位想看的电影、电视剧与综艺。`
      : "在91视频网站搜索电影、电视剧、综艺，输入片名、类型或演员，快速发现想看的内容。",
    keywords: query
      ? `${query},${query}在线观看,91视频网站,影视搜索`
      : "影视搜索,91视频网站,电影搜索,电视剧搜索",
    canonicalPath: "/search",
  });

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const value = input.trim();
    setSearchParams(value ? { q: value } : {});
  };

  const handleReset = () => {
    setFilters(emptyFilters);
    setSort("latest");
  };

  return (
    <div className="w-full px-4 py-8 md:px-8 md:py-10">
      <nav className="mb-4 flex items-center gap-1 text-sm text-foreground-500" aria-label="面包屑">
        <Link to="/" className="transition-colors hover:text-primary-600 cursor-pointer">
          首页
        </Link>
        <i className="ri-arrow-right-s-line" aria-hidden="true"></i>
        <span className="text-foreground-900">搜索</span>
      </nav>

      <div className="rounded-lg border border-background-200 bg-background-100 p-5 md:p-6">
        <h1 className="font-heading text-xl font-extrabold text-foreground-950 md:text-2xl">
          搜索影视资源
        </h1>
        <form
          onSubmit={submit}
          className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center"
        >
          <div className="flex flex-1 items-center gap-2 rounded-md border border-background-300 bg-background-50 px-3 py-2.5 focus-within:border-primary-400">
            <i className="ri-search-line text-lg text-foreground-400" aria-hidden="true"></i>
            <input
              type="text"
              name="q"
              value={input}
              onChange={(event) => setInput(event.target.value)}
              placeholder="输入片名、类型或演员，如：星河、悬疑、沈书言"
              aria-label="搜索关键词"
              className="w-full bg-transparent text-sm text-foreground-900 placeholder:text-foreground-400 outline-none"
            />
          </div>
          <button
            type="submit"
            className="flex items-center justify-center gap-2 rounded-md bg-primary-500 px-6 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 cursor-pointer whitespace-nowrap"
          >
            <i className="ri-search-line text-base" aria-hidden="true"></i>
            搜索
          </button>
        </form>
        {query ? (
          <p className="mt-3 text-sm text-foreground-500">
            当前搜索词：<strong className="text-primary-600">{query}</strong>
          </p>
        ) : null}
      </div>

      {!query ? (
        <div className="mt-6">
          <EmptyState
            icon="ri-search-line"
            title="输入关键词开始搜索"
            description="试试搜索你想看的电影、电视剧或综艺名称，也可以按类型、演员来发现内容。"
          />
        </div>
      ) : matched.length === 0 ? (
        <div className="mt-6">
          <EmptyState
            icon="ri-emotion-sad-line"
            title={`没有找到与“${query}”相关的影视`}
            description="换个关键词试试吧，比如更短的片名、类型名称或演员名字。"
            onReset={() => {
              setInput("");
              setSearchParams({});
            }}
            resetLabel="重新搜索"
          />
        </div>
      ) : (
        <>
          <div className="mt-6 mb-4 flex flex-wrap items-center justify-between gap-3">
            <p className="text-sm text-foreground-600">
              共找到 <strong className="text-primary-600">{results.length}</strong> 条匹配结果
            </p>
          </div>

          <div className="mb-6">
            <FilterBar
              options={options}
              filters={filters}
              onToggle={(dimension, value) =>
                setFilters((prev) => {
                  const list = prev[dimension];
                  const next = list.includes(value)
                    ? list.filter((item) => item !== value)
                    : [...list, value];
                  return { ...prev, [dimension]: next };
                })
              }
              onReset={handleReset}
              sort={sort}
              onSortChange={setSort}
            />
          </div>

          {results.length > 0 ? (
            <div className="grid grid-cols-2 gap-3 md:gap-4 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
              {results.map((item) => (
                <MediaCard key={item.id} item={item} showActors />
              ))}
            </div>
          ) : (
            <EmptyState
              title="当前筛选条件下没有结果"
              description="试试减少筛选条件，或重置筛选查看全部搜索结果。"
              onReset={handleReset}
            />
          )}
        </>
      )}
    </div>
  );
}